

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import { IconExternalLink, IconX } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";

import { Card } from "../Card/Card";
import { useUserLocale } from "@/hooks/useUserLocale";
import { useEventTimezone } from "@/providers/EventProvider";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";
import type {
  AppointmentRequest,
  AvailabilityWindow,
  GuestAppointmentSummary,
} from "@/lib/slotting/types";

interface PRequestDetailsProps {
  request: AppointmentRequest;
  eventId: string;
  offeringName?: string;
  treatmentMap: Map<string, string>;
  onClose: () => void;
}

// `tz` = event timezone for availability/appointment WALL times (startDt/endDt
// are real UTC instants). Omit tz for audit moments (createdAt/updatedAt) where
// the admin's browser-local frame is the conventional display.
function formatDate(dt: string, locale: string, tz?: string): string {
  try {
    return new Date(dt).toLocaleDateString(locale, {
      timeZone: tz,
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dt;
  }
}

function formatTime(dt: string, locale: string, tz?: string): string {
  try {
    return new Date(dt).toLocaleTimeString(locale, {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatTimeRange(
  start: string,
  end: string,
  locale: string,
  tz?: string,
): string {
  const s = formatTime(start, locale, tz);
  const e = formatTime(end, locale, tz);
  return s && e ? `${s} – ${e}` : "";
}

export const PRequestDetails: React.FC<PRequestDetailsProps> = ({
  request,
  eventId,
  offeringName,
  treatmentMap,
  onClose,
}) => {
  const t = useTranslations("slotting");
  useSuppressCoach(); // hide bottom-right floaters over this modal
  const locale = useUserLocale();
  const tz = useEventTimezone();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  const treatmentNames = request.treatmentIds
    .map((id) => treatmentMap.get(id))
    .filter((name): name is string => Boolean(name));

  const calendarHref = `/event/${eventId}/slotting/${request.offeringId}?requestId=${request.appointmentRequestId}`;

  return createPortal(
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 pt-4 sm:p-8 z-[60] sm:pr-[calc(2rem+var(--ai-rail-push))] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="w-full sm:max-w-[560px] flex flex-col min-h-0 max-h-full"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
      >
        <Card
          padding="p-6"
          surfaceColor="surface-card"
          rounded="rounded-t-3xl sm:rounded-3xl"
          width="w-full"
          height="h-auto max-h-full"
          className="z-50 overflow-hidden"
          justify="gap-5"
          header={t("requestDetails.title")}
          icon={IconX}
          iconActionOnClick={onClose}
        >
          <div className="flex flex-col gap-4 overflow-y-auto min-h-0 flex-1">
            {request.archived && (
              <div className="flex flex-col gap-1 surface-tertiary px-4 py-3 rounded-lg border outline-quaternary">
                <p className="body-3-medium font-tertiary">
                  {t("requestDetails.archived")}
                </p>
              </div>
            )}

            {/* Offering */}
            <DetailRow
              label={t("requestDetails.offering")}
              value={offeringName ?? request.offeringId}
            />

            {/* Treatments */}
            <div className="flex flex-col gap-1.5 min-w-0">
              <p className="body-4-regular font-tertiary">
                {t("requestDetails.treatments")}
              </p>
              {treatmentNames.length > 0 ? (
                <div className="flex flex-row flex-wrap gap-1.5">
                  {treatmentNames.map((name, i) => (
                    <span
                      key={i}
                      className="surface-tertiary px-2 py-1 rounded body-4-regular"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="body-3-medium font-tertiary">
                  {t("noTreatmentsSpecified")}
                </p>
              )}
            </div>

            {/* Availability windows */}
            <div className="flex flex-col gap-1.5 min-w-0">
              <p className="body-4-regular font-tertiary">
                {t("requestDetails.availability")}
              </p>
              {request.availability && request.availability.length > 0 ? (
                <ul className="flex flex-col gap-1">
                  {request.availability.map((w: AvailabilityWindow, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between surface-tertiary px-3 py-2 rounded-lg body-3-regular"
                    >
                      <span className="truncate">{formatDate(w.startDt, locale, tz)}</span>
                      <span className="font-tertiary shrink-0 body-4-regular">
                        {formatTimeRange(w.startDt, w.endDt, locale, tz)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="body-3-medium font-tertiary">
                  {t("noAvailabilities")}
                </p>
              )}
            </div>

            {/* Notes */}
            {request.notes && (
              <DetailRow label={t("note")} value={request.notes} multiline />
            )}

            {/* Existing generated appointments */}
            {request.guestAppointments && request.guestAppointments.length > 0 && (
              <div className="flex flex-col gap-1.5 min-w-0">
                <p className="body-4-regular font-tertiary">
                  {t("requestDetails.scheduled")} ({request.guestAppointments.length})
                </p>
                <ul className="flex flex-col gap-1">
                  {request.guestAppointments.map(
                    (appt: GuestAppointmentSummary) => (
                      <li
                        key={appt.appointmentId}
                        className="flex items-center justify-between surface-tertiary px-3 py-2 rounded-lg body-3-regular"
                      >
                        <span className="truncate">
                          {formatDate(appt.startDt, locale, tz)}
                        </span>
                        <span className="font-tertiary shrink-0 body-4-regular">
                          {formatTimeRange(appt.startDt, appt.endDt, locale, tz)}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}

            {/* Timestamps */}
            <div className="flex flex-row gap-6 justify-between pt-1">
              <DetailRow
                label={t("requestDetails.created")}
                value={formatDate(request.createdAt, locale)}
              />
              <DetailRow
                label={t("requestDetails.updated")}
                value={formatDate(request.updatedAt, locale)}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-row justify-end pt-2 border-t outline-quaternary">
            <Link
              to={calendarHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-row items-center gap-1.5 px-3 py-2 rounded-lg border outline-quaternary body-3-medium hover:surface-secondary transition-colors"
            >
              <IconExternalLink size={14} />
              {t("requestDetails.openInCalendar")}
            </Link>
          </div>
        </Card>
      </motion.div>
    </motion.div>,
    document.body,
  );
};

type DetailRowProps = {
  label: string;
  value: string;
  multiline?: boolean;
};

function DetailRow({ label, value, multiline }: DetailRowProps) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <p className="body-4-regular font-tertiary">{label}</p>
      <p
        className={`body-3-medium ${
          multiline ? "whitespace-pre-wrap" : "truncate"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
